import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, GripVertical } from 'lucide-react';
import { useTutorStore } from '@shared/state';
import { TUTOR_PANEL_MIN_WIDTH, TUTOR_PANEL_MAX_WIDTH } from '@shared/state/tutor-store';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import { TutorModeButtons } from './TutorModeButtons';
import { TutorConversation } from './TutorConversation';
import { TutorLimitWarning } from './TutorLimitWarning';
import { VoiceTutorControls } from '../voice/VoiceTutorControls';

export function TutorFAB() {
  const openPanel = useTutorStore((s) => s.openPanel);
  const isOpen = useTutorStore((s) => s.isOpen);

  if (isOpen) return null;

  return (
    <motion.button
      type="button"
      onClick={() => openPanel()}
      aria-label="Open Professor Cogni"
      className="fixed bottom-6 right-6 z-50 rounded-full
        bg-indigo-100 border-2 border-indigo-300 shadow-glow-indigo
        flex items-center justify-center cursor-pointer
        hover:border-indigo-400 hover:shadow-lg transition-shadow"
      style={{ width: 72, height: 72 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.96 }}
    >
      <img src="/CogHead.png" alt="" className="w-14 h-14 object-contain pointer-events-none" />
    </motion.button>
  );
}

export function TutorPanel() {
  const isOpen = useTutorStore((s) => s.isOpen);
  const rawClosePanel = useTutorStore((s) => s.closePanel);
  const sendQuery = useTutorStore((s) => s.sendQuery);
  const isLoading = useTutorStore((s) => s.isLoading);
  const queryLimits = useTutorStore((s) => s.queryLimits);
  const highlightedText = useTutorStore((s) => s.highlightedText);
  const currentSession = useTutorStore((s) => s.currentSession);
  const adaptationMode = useTutorStore((s) => s.adaptationMode);
  const exitAdaptationMode = useTutorStore((s) => s.exitAdaptationMode);
  const panelWidth = useTutorStore((s) => s.panelWidth);
  const setPanelWidth = useTutorStore((s) => s.setPanelWidth);

  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [panelOpenTime] = useState(() => Date.now());

  // Resize is desktop-only; on mobile the panel is a full-width bottom sheet.
  const [isResizable, setIsResizable] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = () => setIsResizable(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
    if (clientX == null) return;
    dragStateRef.current = { startX: clientX, startWidth: panelWidth };
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function clampToViewport(w: number): number {
      const viewportCap = Math.min(TUTOR_PANEL_MAX_WIDTH, window.innerWidth - 80);
      return Math.max(TUTOR_PANEL_MIN_WIDTH, Math.min(viewportCap, w));
    }

    const onMove = (ev: MouseEvent | TouchEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      const x = 'touches' in ev ? ev.touches[0]?.clientX : ev.clientX;
      if (x == null) return;
      // Panel is right-anchored: dragging left (smaller clientX) widens it.
      const delta = state.startX - x;
      setPanelWidth(clampToViewport(state.startWidth + delta));
    };
    const onEnd = () => {
      const state = dragStateRef.current;
      if (state) {
        // Re-persist the final value so localStorage reflects drag-end width.
        setPanelWidth(clampToViewport(useTutorStore.getState().panelWidth), { persist: true });
      }
      dragStateRef.current = null;
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
  }, [panelWidth, setPanelWidth]);

  const handleResizeDoubleClick = useCallback(() => {
    setPanelWidth(400, { persist: true });
  }, [setPanelWidth]);

  const closePanel = useCallback(() => {
    trackEvent({
      name: ANALYTICS_EVENTS.TUTOR_PANEL_CLOSED,
      properties: {
        ...getBaseProperties(),
        sessionDurationMs: Date.now() - panelOpenTime,
        queriesSent: currentSession?.queriesInSession ?? 0,
      },
    }).catch(() => {});
    if (adaptationMode) exitAdaptationMode();
    rawClosePanel();
  }, [rawClosePanel, panelOpenTime, currentSession, adaptationMode, exitAdaptationMode]);

  const canQuery = queryLimits?.canQuery !== false;

  function handleSend() {
    const query = inputValue.trim();
    if (!query || isLoading || !canQuery) return;
    // Behavioural signal (weight 0.7) — freeform query. Skip profile-discussion
    // queries, which aren't tied to a learning path.
    const { pathId, activeNodeId } = useTutorStore.getState();
    if (!adaptationMode && pathId) {
      trackEvent({
        name: ANALYTICS_EVENTS.TUTOR_QUERY_SENT,
        properties: {
          ...getBaseProperties(),
          pathId,
          nodeId: activeNodeId,
          mode: 'freeform',
          conceptId: null,
          queryLength: query.length,
          queryNumber: (currentSession?.queriesInSession ?? 0) + 1,
        },
      }).catch(() => {});
    }
    sendQuery(query);
    setInputValue('');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Mobile scrim */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closePanel}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            style={isResizable ? { width: panelWidth } : undefined}
            className="fixed right-0 top-0 bottom-0 z-50 w-full
              bg-dark-surface border-l border-dark-border
              flex flex-col
              max-sm:top-auto max-sm:h-[85vh] max-sm:rounded-t-2xl max-sm:border-t max-sm:border-l-0"
          >
            {/* Resize handle (desktop only): a wide invisible hit strip on
                the left edge, with a persistent grip "tab" poking out so the
                affordance is discoverable without hover. */}
            {isResizable && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize Professor Cogni panel"
                title="Drag to resize · double-click to reset"
                onMouseDown={handleResizeStart}
                onTouchStart={handleResizeStart}
                onDoubleClick={handleResizeDoubleClick}
                className="group absolute left-0 top-0 bottom-0 w-3 -translate-x-1/2
                  cursor-col-resize z-10 flex items-center justify-center"
              >
                {/* Full-height thin indicator line that brightens on hover/drag */}
                <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-px transition-colors
                  ${isDragging ? 'bg-gold-400' : 'bg-dark-border group-hover:bg-gold-400/70'}`} />
                {/* Persistent grip tab — always visible, grows on hover. Gold
                    outlines match the learning sidebar collapse button. */}
                <div className={`relative flex items-center justify-center
                  rounded-full border shadow-modal transition-all duration-150
                  ${isDragging
                    ? 'w-6 h-14 bg-gold-500 border-gold-400 text-dark-bg shadow-glow-gold'
                    : 'w-5 h-12 bg-dark-elevated border-gold-500 text-gold-500 group-hover:w-6 group-hover:h-14 group-hover:bg-gold-500 group-hover:border-gold-400 group-hover:text-dark-bg group-hover:shadow-glow-gold'}
                `}>
                  <GripVertical size={14} strokeWidth={2.5} className="pointer-events-none" />
                </div>
              </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border shrink-0">
              <div className="flex items-center gap-2">
                <img src="/CogHead.png" alt="Professor Cogni" className="w-7 h-7 object-contain" />
                <div>
                  <span className="font-heading font-semibold text-sm">Professor Cogni</span>
                  <span className="text-xs text-content-muted font-body ml-1.5">
                    {adaptationMode ? 'Profile Discussion' : 'your personal tutor'}
                  </span>
                </div>
              </div>
              <button
                onClick={closePanel}
                className="p-1.5 rounded-full text-content-muted hover:text-content hover:bg-dark-hover transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Mode buttons — hidden in adaptation mode */}
            {!adaptationMode && (
              <div className="border-b border-dark-border shrink-0">
                <TutorModeButtons />
              </div>
            )}

            {/* Highlighted text context */}
            {highlightedText && (
              <div className="px-3 py-2 border-b border-dark-border bg-gold-500/5 shrink-0">
                <p className="text-[10px] text-gold-500 font-body uppercase tracking-wider mb-1 font-semibold">Selected text</p>
                <p className="text-xs text-content-secondary font-body line-clamp-2 italic">
                  "{highlightedText}"
                </p>
              </div>
            )}

            {/* Conversation */}
            <TutorConversation />

            {/* Limit warning */}
            <TutorLimitWarning />

            {/* Input bar */}
            <div className="px-3 py-3 border-t border-dark-border shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={canQuery ? (adaptationMode ? 'Discuss your profile or learning preferences...' : 'Ask Professor Cogni anything...') : 'Query limit reached'}
                  disabled={!canQuery || isLoading}
                  className="flex-1 px-3 py-2 rounded bg-dark-subtle border border-dark-border
                    text-sm text-content font-body
                    placeholder:text-content-dim
                    focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500
                    disabled:opacity-40 disabled:cursor-not-allowed"
                />
                {canQuery && <VoiceTutorControls />}
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading || !canQuery}
                  className="p-2 rounded bg-indigo-500 text-white
                    hover:bg-[#7E70EF] transition-colors cursor-pointer
                    disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
